import type { ReactNode } from 'react';
import type { Components } from 'react-markdown';
import { Activity } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { NormalizedMcc } from '@ballerine/common';

import { ctw } from '@/common/utils/ctw';
import { CopyButton } from '@/components';
import { ReportWrapper } from '../components/ReportWrapper';

type OperationalActivitySectionProps = {
  lineOfBusiness?: string | null;
  lineOfBusinessReasoning?: string | null;
  mcc?: NormalizedMcc | null;
  alternatives?: NormalizedMcc[] | null;
  /** When false, alternative MCCs are not shown (gated by the alternative-MCC feature flag). */
  showAlternatives?: boolean;
};

const NOT_AVAILABLE = 'N/A';

/**
 * Shared "Operational Activity" section rendered in both Digital Footprint and Point of Sale
 * reports. Surfaces the Ballerine-assigned Line of Business + Primary MCC (and optional
 * Alternative MCCs) so the assigned classification is clearly distinguished from the header.
 *
 * Always renders — even when data is null (offline site) it shows N/A placeholders rather than
 * hiding, so the widget is a stable anchor in the report.
 */
export const OperationalActivitySection = ({
  lineOfBusiness,
  lineOfBusinessReasoning,
  mcc,
  alternatives,
  showAlternatives = false,
}: OperationalActivitySectionProps) => {
  const visibleAlternatives = showAlternatives
    ? (alternatives ?? []).filter(alternative => Boolean(alternative?.code))
    : [];

  return (
    <ReportWrapper
      title="Operational Activity"
      Icon={Activity}
      tooltip="Business operations analysis including line of business and merchant category classification."
      showTooltip={false}
    >
      <p className="-mt-3 mb-4 text-sm text-slate-500">
        Assigned based on Visa Merchant Data Standards Manual
      </p>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <OperationalCard badge="Line Of Business" variant="primary">
          <ItemBody title={lineOfBusiness} reasoning={lineOfBusinessReasoning} />
        </OperationalCard>

        <OperationalCard badge="Primary MCC" variant="primary">
          <ItemBody
            title={mcc?.code}
            description={mcc?.category}
            reasoning={mcc?.reasoning}
            fallbackNote="Website is offline"
          />
        </OperationalCard>
      </div>

      {visibleAlternatives.length > 0 && (
        <OperationalCard badge="Alternative MCCs" variant="secondary" className="mt-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {visibleAlternatives.map(alternative => (
              <ItemBody
                key={[alternative.code, alternative.category].filter(Boolean).join(' - ')}
                title={alternative.code}
                description={alternative.category}
                reasoning={alternative.reasoning}
              />
            ))}
          </div>
        </OperationalCard>
      )}
    </ReportWrapper>
  );
};

const OperationalCard = ({
  badge,
  variant,
  className,
  children,
}: {
  badge: string;
  variant: 'primary' | 'secondary';
  className?: string;
  children: ReactNode;
}) => (
  <div className={ctw('space-y-4 rounded-2xl border p-5', className)}>
    <div
      className={ctw(
        'inline-block rounded-sm px-2 py-0.5 text-sm font-medium uppercase tracking-tight',
        variant === 'primary' ? 'bg-blue-600/20 text-blue-600' : 'bg-slate-400/20 text-slate-400',
      )}
    >
      {badge}
    </div>
    {children}
  </div>
);

const ItemBody = ({
  title,
  description,
  reasoning,
  fallbackNote,
}: {
  title?: string | null;
  description?: string | null;
  reasoning?: string | null;
  fallbackNote?: string;
}) => {
  const hasTitle = Boolean(title);

  return (
    <div>
      <p data-testid="operational-activity-item" className="text-xl font-semibold text-slate-900">
        {title || NOT_AVAILABLE}
      </p>

      {description && (
        <div className="flex items-start gap-1 text-justify text-sm leading-relaxed">
          <span className="font-medium text-slate-900">{description}</span>
          <CopyButton
            className="inline"
            textToCopy={[title, description].filter(Boolean).join(' - ')}
          />
        </div>
      )}

      {!hasTitle && !description && fallbackNote && (
        <p className="text-sm leading-relaxed text-neutral-500">{fallbackNote}</p>
      )}

      {reasoning && (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {reasoning}
        </ReactMarkdown>
      )}
    </div>
  );
};

const markdownComponents: Components = {
  p: ({ node, ...props }) => (
    <p className="my-3 text-justify text-sm leading-6 text-slate-800" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="mb-1 mt-2 text-lg font-semibold text-slate-800" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="mb-1 mt-2 text-base font-semibold text-slate-800" {...props} />
  ),
  ul: ({ node, ...props }) => <ul className="my-3 list-disc space-y-2 pl-6" {...props} />,
  ol: ({ node, ...props }) => <ol className="my-3 list-decimal space-y-2 pl-6" {...props} />,
  li: ({ node, ...props }) => <li className="max-w-[90ch] pl-1.5 text-sm leading-6" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="mx-1 inline-block rounded-full bg-indigo-50 px-2 py-0 text-[0.7rem] text-neutral-400 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
};
